import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Press feedback (Task #7899): every variant gets an instant :active
// treatment — a slight scale-down plus a background shift via the shared
// `.active-elevate-2` overlay (index.css), which layers a light/dark-aware
// tint over the variant's own background so it reads in both themes.
// `disabled:pointer-events-none` means :active can never fire on disabled
// buttons, so the press styles don't fight the disabled treatment.
// Transitions are an explicit transform/color/opacity/shadow list — never
// `transition-all` — so taps and scrolling stay off the layout path.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold cursor-pointer transition-[transform,background-color,color,border-color,opacity,box-shadow] duration-200 active:scale-[0.97] active-elevate-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-default disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
           "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-border bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // Text-style link: no surface, so skip the scale + elevate press kit.
        link: "text-primary underline-offset-4 hover:underline active:scale-100 no-default-active-elevate",
        accent:
          "bg-primary text-primary-foreground hover:bg-primary/90",
      },
      size: {
        default: "min-h-10 px-6 py-2.5",
        sm: "min-h-8 px-4 text-xs",
        lg: "min-h-12 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
